/**
 * support_inbox_controller.js — staff support inside the Chip widget, and the
 * thread list of the admin support page (Stimulus identifier support-inbox).
 *
 * In the widget (templates/components/support_pane.html) it decides, the
 * first time the pane is visible, which region shows: GET /api/support/me
 * says whether the viewer is staff. A customer gets their own chat (the
 * response is handed to that support-chat instance); staff get the thread
 * list (support_inbox_list.html) and open one thread at a time in a
 * staff-mode support-chat under its header (support_staff_header.html).
 * Without a customer region (the admin page, templates/admin/support_inbox.html)
 * it starts as staff at once, opens initialConversation if given, and with
 * stacked=false keeps the list beside the open thread.
 *
 * The rows are the server's (GET /api/support/threads?filter=...), updated
 * between fetches by live 'chippie:support' events (applyThreadEvent in
 * _support_inbox.js) and refetched when the tab is refocused. A staff deep
 * link's thread comes from chat-tabs (takePendingConversation).
 *
 * "New conversation" searches people (GET /api/support/customers). Someone
 * who already has a thread gets it opened; anyone else gets a draft: the
 * header from draftThread() over an empty staff-mode chat, whose first send
 * creates the thread ('support-chat:sent', adopted in threadChanged).
 */
import { Controller } from "@hotwired/stimulus";
import { supportRequest } from "./_support_api.js";
import { applyThreadEvent, draftThread, rowFromThread, sortRows } from "./_support_inbox.js";
import { escapeHtml } from "./_support_messages.js";

const EMPTY_TEXT = { waiting: 'Nobody is waiting.', mine: 'No threads are yours.' };
const LIST_EVENTS = ['support_message', 'support_thread'];   // typing and read leave rows alone
const SEARCH_DEBOUNCE_MS = 250;

export default class extends Controller {
    static targets = ['skeleton', 'customerRegion', 'staffRegion', 'listView', 'threadView', 'staffThread',
                      'placeholder', 'filter', 'list', 'empty', 'headerName', 'headerMeta',
                      'headerClaim', 'resolveButton', 'tickets', 'browse', 'search',
                      'searchInput', 'searchResults', 'searchEmpty'];
    static values = {
        api: String,
        me: Number,
        stacked: { type: Boolean, default: true },
        initialConversation: Number,   // 0: none (the admin page's ?conversation=<id>)
    };

    connect() {
        this._filter = this._filter || 'waiting';
        this.threads = this.threads || [];
        this._onVisibility = () => {
            if (this._staff && document.visibilityState === 'visible') this.loadList();
        };
        document.addEventListener('visibilitychange', this._onVisibility);
        this._observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this._start();
        });
        this._observer.observe(this.element);
        // Hovering or pressing the widget's launcher is the intent to open it:
        // start the /me round trip then, so the pane is ready when it shows,
        // without spending a request on every page load.
        this._launcher = this.element.closest('.pchat')?.querySelector('.pchat-launcher');
        this._onIntent = () => this._start();
        this._launcher?.addEventListener('pointerenter', this._onIntent);
        this._launcher?.addEventListener('pointerdown', this._onIntent);
    }

    disconnect() {
        document.removeEventListener('visibilitychange', this._onVisibility);
        this._launcher?.removeEventListener('pointerenter', this._onIntent);
        this._launcher?.removeEventListener('pointerdown', this._onIntent);
        this._observer.disconnect();
        clearTimeout(this._searchTimer);
    }

    async _start() {
        if (this._started) return;
        this._started = true;
        if (!this.hasCustomerRegionTarget) {
            this._showStaff();
            return;
        }
        let me;
        try {
            me = await this._request('GET', '/me');
        } catch (_error) {
            this._started = false;   // try again next time the pane shows
            return;
        }
        if (me.is_staff) {
            this._showStaff();
        } else {
            this._chatIn(this.customerRegionTarget).loadCustomer(me);
            this.customerRegionTarget.hidden = false;
            this.skeletonTarget.hidden = true;
        }
    }

    _showStaff() {
        this._staff = true;
        if (this.hasSkeletonTarget) this.skeletonTarget.hidden = true;
        if (this.hasStaffRegionTarget) this.staffRegionTarget.hidden = false;
        this.loadList();
        const tabsElement = this.element.closest('[data-controller~="chat-tabs"]');
        const tabs = tabsElement && this.application.getControllerForElementAndIdentifier(
            tabsElement, 'chat-tabs');
        const pending = (tabs && tabs.takePendingConversation()) || this.initialConversationValue;
        if (pending) this.open(pending);
    }

    // ==================== List ====================

    async loadList() {
        try {
            const data = await this._request('GET', `/threads?filter=${this._filter}`);
            this.threads = data.threads;
            this._renderList();
        } catch (_error) {
            /* offline: keep the rows shown */
        }
    }

    // data-action="click->support-inbox#filter" on each filter chip (data-filter).
    filter(event) {
        this._filter = event.currentTarget.dataset.filter;
        this.filterTargets.forEach((chip) => {
            chip.setAttribute('aria-pressed', String(chip === event.currentTarget));
        });
        this.loadList();
    }

    // data-action="chippie:support@document->support-inbox#receive"
    receive({ detail }) {
        if (!this._staff || !LIST_EVENTS.includes(detail?.type)) return;
        const next = applyThreadEvent(this.threads, detail);
        if (next === null) {
            this.loadList();   // a thread this view does not have yet
            return;
        }
        this.threads = next;
        this._renderList();
        if (this._current && detail.data.conversation_id === this._currentId) {
            this._current = applyThreadEvent([this._current], detail)[0];
            this._renderHeader();
        }
    }

    _renderList() {
        const rows = sortRows(this.threads, this._filter).map((t) => rowFromThread(t, this.meValue));
        this.listTarget.innerHTML = rows.map((row) => this._rowHtml(row)).join('');
        this.emptyTarget.textContent = EMPTY_TEXT[this._filter] || 'No threads here.';
        this.emptyTarget.hidden = rows.length > 0;
    }

    _rowHtml(row) {
        const current = row.id === this._currentId ? ' is-current' : '';
        const business = row.business
            ? `<span class="support-inbox-business">${escapeHtml(row.business)}</span>` : '';
        const waiting = row.waiting ? `<span class="support-inbox-wait">${row.waiting}</span>` : '';
        const unread = row.unread ? `<span class="support-inbox-unread">${row.unread}</span>` : '';
        return `<button type="button" class="support-inbox-row${current}"
            data-action="click->support-inbox#openRow" data-support-inbox-id-param="${row.id}">
            <span class="support-inbox-line"><strong>${escapeHtml(row.name)}</strong>
              <span class="support-claim">${escapeHtml(row.claim)}</span></span>
            ${business}
            <span class="support-inbox-line"><span class="support-inbox-preview">${escapeHtml(row.preview)}</span>
              ${waiting}${unread}</span>
        </button>`;
    }

    // ==================== One thread ====================

    openRow({ params }) {
        this.open(params.id);
    }

    async open(conversationId) {
        this._currentId = conversationId;
        this._current = null;
        this.threads = this.threads.map((t) => (
            t.conversation_id === conversationId ? { ...t, unread: 0 } : t));
        this._renderList();
        this._showThread(true);
        this._renderHeader();
        try {
            await this._chatIn(this.staffThreadTarget).showConversation(conversationId);
        } catch (error) {
            window.showErrorMessage(`Could not open the conversation: ${error.message}`);
            this.back();
        }
    }

    back() {
        this._currentId = null;
        this._current = null;
        this._showThread(false);
        this.loadList();
    }

    // Stacked (the widget) shows the list or a thread; side by side (the admin
    // page) keeps the list, and its CSS stacks on a phone off the class.
    _showThread(showing) {
        this.threadViewTarget.hidden = !showing;
        if (this.stackedValue) this.listViewTarget.hidden = showing;
        if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = showing;
        this.element.classList.toggle('support-thread-open', showing);
    }

    // data-action="support-chat:loaded->support-inbox#threadLoaded"
    threadLoaded({ detail }) {
        if (!detail?.thread) return;
        this._current = detail.thread;
        this._renderHeader();
        this._renderTickets(detail.tickets || []);
    }

    // data-action="support-chat:sent->support-inbox#threadChanged": a reply takes
    // the claim, and a draft's first send has just created its thread.
    async threadChanged({ detail }) {
        if (!this._staff || !detail?.conversationId) return;
        if (this._drafting()) this._currentId = detail.conversationId;
        if (detail.conversationId !== this._currentId) return;
        let data;
        try {
            data = await this._request('GET', `/conversations/${this._currentId}`);
        } catch (_error) {
            return;   // the reply is saved; the header catches up on the next open
        }
        this._current = data.thread;
        this._renderHeader();
        this._replaceRow(data.thread);
    }

    async resolve() {
        let data;
        try {
            data = await this._request('POST', `/conversations/${this._currentId}/resolve`);
        } catch (error) {
            window.showErrorMessage(`Could not mark the conversation resolved: ${error.message}`);
            return;
        }
        this._current = data.thread;
        this._renderHeader();
        this.loadList();
    }

    // A thread this view does not hold (one just created) is refetched into it.
    _replaceRow(thread) {
        if (!this.threads.some((t) => t.conversation_id === thread.conversation_id)) {
            this.loadList();
            return;
        }
        this.threads = this.threads.map((t) => (
            t.conversation_id === thread.conversation_id ? thread : t));
        this._renderList();
    }

    _renderHeader() {
        const thread = this._current;
        this.headerNameTarget.textContent = thread ? thread.customer_name : '';
        this.headerMetaTarget.textContent = thread
            ? [thread.customer_email, thread.business_name,
                thread.customer_deleted && 'Account deleted'].filter(Boolean).join(' · ') : '';
        this.headerClaimTarget.textContent = thread ? rowFromThread(thread, this.meValue).claim : '';
        this.resolveButtonTarget.hidden = !thread || !thread.conversation_id
            || thread.status === 'resolved';
        if (!thread) this.ticketsTarget.hidden = true;
    }

    _drafting() {
        return Boolean(this._current) && !this._current.conversation_id;
    }

    // ==================== New conversation ====================

    // data-action="click->support-inbox#newConversation"
    newConversation() {
        this.browseTarget.hidden = true;
        this.searchTarget.hidden = false;
        this.searchInputTarget.value = '';
        this._renderCustomers([], '');
        this.searchInputTarget.focus();
    }

    // data-action="click->support-inbox#closeSearch" (Cancel, and Escape in the box)
    closeSearch() {
        clearTimeout(this._searchTimer);
        this.searchTarget.hidden = true;
        this.browseTarget.hidden = false;
    }

    // data-action="input->support-inbox#searchCustomers"
    searchCustomers() {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => this._findCustomers(), SEARCH_DEBOUNCE_MS);
    }

    async _findCustomers() {
        const query = this.searchInputTarget.value.trim();
        if (!query) {
            this._renderCustomers([], '');
            return;
        }
        let data;
        try {
            data = await this._request('GET', `/customers?q=${encodeURIComponent(query)}`);
        } catch (_error) {
            return;   // offline: keep what is shown
        }
        if (query !== this.searchInputTarget.value.trim()) return;   // typed on since
        this._renderCustomers(data.customers, query);
    }

    _renderCustomers(customers, query) {
        this._customers = customers;
        this.searchResultsTarget.innerHTML = customers.map((c) => `<button type="button"
            class="support-inbox-row" data-action="click->support-inbox#pickCustomer"
            data-support-inbox-id-param="${c.user_id}">
            <span class="support-inbox-line"><strong>${escapeHtml(c.name)}</strong>
              ${c.conversation_id ? '<span class="support-claim">Has a conversation</span>' : ''}</span>
            <span class="support-inbox-business">${escapeHtml(c.email)}</span>
        </button>`).join('');
        this.searchEmptyTarget.hidden = !query || customers.length > 0;
    }

    // data-action="click->support-inbox#pickCustomer": their thread, or a draft.
    pickCustomer({ params }) {
        const customer = (this._customers || []).find((c) => c.user_id === params.id);
        if (!customer) return;
        this.closeSearch();
        if (customer.conversation_id) {
            this.open(customer.conversation_id);
            return;
        }
        this._currentId = null;
        this._current = draftThread(customer);
        this._renderList();
        this._showThread(true);
        this._renderHeader();
        this._renderTickets([]);
        this._chatIn(this.staffThreadTarget).startConversation(customer.user_id);
    }

    _renderTickets(tickets) {
        const manageUrl = escapeHtml(this.ticketsTarget.dataset.manageUrl);
        this.ticketsTarget.hidden = tickets.length === 0;
        this.ticketsTarget.innerHTML = tickets.map((ticket) => `<div class="support-ticket">
            <a href="${manageUrl}">${escapeHtml(ticket.type_label)} #${ticket.id}</a>
            <span class="support-ticket-title">${escapeHtml(ticket.title)}</span>
            <span class="support-ticket-status">${escapeHtml(ticket.status)}</span></div>`).join('');
    }

    _chatIn(container) {
        const element = container.querySelector('[data-controller~="support-chat"]');
        return this.application.getControllerForElementAndIdentifier(element, 'support-chat');
    }

    _request(method, path) {
        return supportRequest(this.apiValue, method, path);
    }
}
